import { useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { AppLayoutContext } from '../../providers/AppLayout'

import { AppPermissions } from '../../models/AppPermissions.model'

import UserService from '../../services/UserService/UserService'
import { ConfigService } from '../../services/ConfigService/ConfigService'

import {
  Stack,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText,
} from '@mui/material'
import Loading from '../../components/Loading/Loading.component'
import { AppStateContext } from '../../providers/AppStateContext'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../providers/SnackbarContext'
import DetailInput from '../../components/DetailInput/DetailInput.component'

interface ConfigType {
  ftEnabled?: string
  ftForced?: string
  precipitationLookbackDays?: string | null
  [key: string]: string | null | undefined
}

const ConfigAdmin = (): JSX.Element => {
  const [tempConfig, setTempConfig] = useState<ConfigType>({})

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [listError, setListError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editConfig])

  useEffect(() => {
    AppLayoutContext.setPageName('Config')
  }, [])

  const queryClient = useQueryClient()

  const configService = useMemo(() => new ConfigService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingConfigs, data: configsResponse } = useQuery(
    ['ConfigService.get', AppStateContext.getRegion()],
    () => configService.get(),
    {
      onError: (err) => {
        setListError(typeof err === 'string' ? err : 'An error occurred while getting config.')
        console.error(err)
      },
    },
  )

  useEffect(() => {
    if (configsResponse) {
      setTempConfig(configsResponse)
    }
  }, [configsResponse])

  const saveConfigMutation = useMutation(
    (mutationParams: { payload: Record<string, string | null> }) => {
      setIsSubmitting(true)
      // Convert empty string to null for precipitationLookbackDays before sending
      const payload = { ...mutationParams.payload }
      if (payload.precipitationLookbackDays === '') {
        payload.precipitationLookbackDays = null
      }
      return configService.save(payload)
    },
    {
      onSuccess: () => {
        SnackbarContext.show('Config saved successfully!', 'success', APP_UPDATE_NOTICE)

        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)
      },
      onError: (err) => {
        SnackbarContext.show(`Config failed to save: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const handleSubmit = () => {
    // Convert undefined values to null to satisfy Record<string, string | null>
    const sanitizedConfig: Record<string, string | null> = Object.fromEntries(
      Object.entries(tempConfig).map(([key, value]) => [key, value === undefined ? null : value]),
    )
    saveConfigMutation.mutate({ payload: sanitizedConfig })
  }

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Config
        </Typography>
        {(isLoadingConfigs || isSubmitting) && !listError && <Loading sx={{ py: 20 }} />}
        {listError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{listError}</Typography>
          </Stack>
        )}
        {!isLoadingConfigs &&
          !isSubmitting &&
          !listError &&
          (!hasWritePermissions ? (
            Object.entries(tempConfig).map(([key, value]) => (
              <Typography variant="body1" component="p" sx={{ mb: 2 }}>
                {key}: {value}
              </Typography>
            ))
          ) : (
            <>
              <Stack spacing={2} sx={{ my: 2 }}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="config-ftenabled-select-label">Freeze/Thaw Enabled</InputLabel>
                  <Select
                    labelId="config-ftenabled-select-label"
                    id="config-ftenabled-select"
                    value={tempConfig.ftEnabled === undefined ? 'false' : tempConfig.ftEnabled}
                    label="Freeze/Thaw Enabled"
                    onChange={(e) => {
                      setTempConfig((prev) => ({
                        ...prev,
                        ftEnabled: e.target.value,
                      }))
                    }}
                  >
                    <MenuItem value="true">Enabled</MenuItem>
                    <MenuItem value="false">Disabled</MenuItem>
                  </Select>
                  <FormHelperText>
                    Regional default for Freeze/Thaw algorithm. This is used when trails and trail systems are set to
                    Inherit.
                  </FormHelperText>
                </FormControl>
                <DetailInput<ConfigType>
                  label="Precipitation Lookback Days"
                  field="precipitationLookbackDays"
                  type="number"
                  tempDetail={tempConfig}
                  setTempDetail={setTempConfig}
                  keyDownSubmit={() => undefined}
                  helperText="Used by the Status algorithm to look back at weather records for X days per region. This value is only used if trail and trail system have no value. If not set here, defaults to 7 days. Must be a number."
                />
              </Stack>
              <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                <Button variant="contained" onClick={handleSubmit}>
                  Save
                </Button>
              </Stack>
            </>
          ))}
      </Paper>
    </>
  )
}
export default ConfigAdmin
